<template>
  <div
    class="select-path-question select-path-question--animate select-path-question--fade-in-up select-path-question--active"
  >
    <div class="select-path-question__content select-path-question__content--with-images">
      <ul class="select-path-question__options">
        <li
          v-for="(option, index) in filteredQuestionsByLang"
          @click="goToPath(option)"
          :key="index"
          class="select-path-question__options-item"
        >
          <span v-if="option.image" class="select-path-question__image">
            <div class="select-path-question__image-container">
              <AsyncImage
                :image="getImage(index)"
                :show-images="showImages"
                :current-image-index-to-load="currentImageIndexToLoad"
                @load-next="loadNext()"
                @is-ready="toggleReady(index)"
              />
            </div>
          </span>
          <div class="select-path-question__label-wrap">
            <span
              class="select-path-question__label-text"
              v-html="$t('index.paths.' + option.title)"
            >
            </span>
          </div>
        </li>
      </ul>
    </div>
    <div
      v-if="roadsideAssistanceUrl.length > 0 || serviceAppointmentUrl.length > 0"
      class="select-path-question__helpers"
    >
      <div v-if="serviceAppointmentUrl.length > 0" class="select-path-question__helpers-item">
        <AppIcon icon="serviceAppointment" class="select-path-question__helpers-icon" />
        <div class="select-path-question__helpers-content">
          <span
            class="select-path-question__helpers-title"
            v-html="$t('index.helpers.service_appointment.title')"
          >
          </span>
          <a
            target="_blank"
            :aria-label="$t('index.helpers.service_appointment.title')"
            class="select-path-question__helpers-link"
            v-html="$t('index.helpers.service_appointment.cta')"
            :href="serviceAppointmentUrl"
          >
          </a>
        </div>
      </div>
      <div v-if="roadsideAssistanceUrl.length > 0" class="select-path-question__helpers-item">
        <AppIcon class="select-path-question__helpers-icon" icon="roadsideAssistance" />

        <div class="select-path-question__helpers-content">
          <span
            class="select-path-question__helpers-title"
            v-html="$t('index.helpers.roadside_assistance.title')"
          >
          </span>

          <a
            target="_blank"
            :aria-label="$t('index.helpers.roadside_assistance.title')"
            class="select-path-question__helpers-link"
            v-html="$t('index.helpers.roadside_assistance.cta')"
            :href="roadsideAssistanceUrl"
          >
          </a>
        </div>
      </div>
    </div>
  </div>
</template>
<script lang="ts">
import { defineComponent, PropType, computed } from 'vue'
import { router } from '@inertiajs/vue3'
import { SelectPathQuestionData } from '~types/concierge'
import AsyncImage from '~conciergeUiCommons/components/AsyncImage.vue'
import { useShowImage } from '~conciergeUiCommons/composables/showImage'

export default defineComponent({
  name: 'SelectPathQuestion',
  components: { AsyncImage },
  setup(props) {
    const filteredQuestionsByLang = computed(() =>
      props.questions.filter(question => {
        return question.language === props.lang || question.language == null
      })
    )

    const questionCount = computed(() => filteredQuestionsByLang.value.length)

    const {
      toggleReady,
      getImage,
      loadNext,
      showImages,
      imagesPayload,
      currentImageIndexToLoad,
      firstImagePayloadBatchCount
    } = useShowImage(questionCount.value)

    return {
      filteredQuestionsByLang,
      showImages,
      toggleReady,
      getImage,
      loadNext,
      imagesPayload,
      currentImageIndexToLoad,
      firstImagePayloadBatchCount
    }
  },
  props: {
    questions: {
      type: Array as PropType<SelectPathQuestionData[]>,
      required: true
    },
    roadsideAssistanceUrl: {
      type: String,
      default: ''
    },
    serviceAppointmentUrl: {
      type: String,
      default: ''
    },
    lang: {
      type: String,
      default: 'fr'
    }
  },
  created() {
    this.questions
      .filter(question => {
        return question.language === this.lang || question.language == null
      })
      .forEach((question, index) => {
        this.imagesPayload.set(index, {
          id: index,
          index: index,
          url: question.image.url,
          width: question.image.width,
          height: question.image.height,
          alt: question.title,
          ready: false,
          show: false
        })
      })
  },
  watch: {
    filteredQuestionsByLang(newQuestions: SelectPathQuestionData[]) {
      newQuestions.forEach((question, index) => {
        this.imagesPayload.set(index, {
          id: index,
          index: index,
          url: question.image.url,
          width: question.image.width,
          height: question.image.height,
          alt: question.title,
          ready: false,
          show: false
        })
      })
      this.firstImagePayloadBatchCount = newQuestions.length > 3 ? 3 : newQuestions.length
      this.currentImageIndexToLoad = 0
    }
  },
  methods: {
    goToPath(path: SelectPathQuestionData): void {
      return router.visit(path.url)
    }
  }
})
</script>
